import { atom, getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/** localStorage key for `piiSessionEndTimeAtom` (e.g. MSW `pii/endTime` reads remaining time). */
export const PII_SESSION_END_TIME_STORAGE_KEY = 'piiSessionEndTime';
const PII_TOKEN_KEY = 'piiToken';
const PII_ENABLED_KEY = 'piiEnabled';

/** Fallback when starting a session without a duration from the pii/endTime API. */
export const PII_SESSION_DEFAULT_DURATION_MS = 60 * 60 * 1000;

export const piiSessionEndTimeAtom = atomWithStorage<number>(PII_SESSION_END_TIME_STORAGE_KEY, 0);

/** Increments once per second while a PII session is active; subscribe so countdown UIs stay in sync. */
export const piiSessionCountdownTickAtom = atom(0);

/** Wall clock ms and performance.now() captured together when we sync with Server-Time or local fallback. */
export interface PiiServerTimeSync {
  anchorEpochMs: number;
  anchorPerfMs: number;
}

function createInitialServerTimeSync(): PiiServerTimeSync {
  const perf = typeof globalThis.performance !== 'undefined' ? globalThis.performance.now() : 0;
  return { anchorEpochMs: Date.now(), anchorPerfMs: perf };
}

/**
 * Last sync from idp 2FA responses. Elapsed time uses `performance.now()` so countdowns are not thrown
 * off if the user changes the system clock after sync.
 */
export const piiServerTimeSyncAtom = atom<PiiServerTimeSync>(createInitialServerTimeSync());

/** Current wall-time estimate: anchor + monotonic elapsed since sync (immune to system clock changes after sync). */
export function getEstimatedWallTimeMs(sync: PiiServerTimeSync): number {
  if (typeof globalThis.performance === 'undefined') {
    return Date.now();
  }
  return sync.anchorEpochMs + (globalThis.performance.now() - sync.anchorPerfMs);
}

/** Apply `Server-Time` (seconds since epoch, UTC); if missing/invalid, anchor to local `Date.now()` at this instant. */
export function applyPiiServerTimeSyncFromResponseHeaders(headers: {
  get(name: string): string | null | undefined;
}): void {
  const store = getDefaultStore();
  const perf = typeof globalThis.performance !== 'undefined' ? globalThis.performance.now() : 0;
  const raw = headers.get('Server-Time') ?? headers.get('server-time');
  let anchorEpochMs: number;
  if (raw != null && raw.trim() !== '') {
    const serverSec = Number.parseInt(raw, 10);
    if (Number.isFinite(serverSec)) {
      anchorEpochMs = serverSec * 1000;
      store.set(piiServerTimeSyncAtom, { anchorEpochMs, anchorPerfMs: perf });
      return;
    }
  }
  anchorEpochMs = Date.now();
  store.set(piiServerTimeSyncAtom, { anchorEpochMs, anchorPerfMs: perf });
}

/** Persisted PII token; use for session timers — derived `piiTokenAtom` is null when expired. */
export const piiTokenStorageAtom = atomWithStorage<string | null>(PII_TOKEN_KEY, null);

export const piiEnabledAtom = atomWithStorage<boolean>(PII_ENABLED_KEY, false);

/** Absolute timestamp (ms) when the PII session expires; derived from pii/endTime `endTime` (duration) + Date.now(). */
export type PiiTokenSetValue = string | null | false | { token: string; sessionDurationMs: number };

/**
 * PII token from useValidatePIISession API. When session is valid, returns the token string; otherwise null.
 * Set with the token string from the API to enable PII, or null/false to end the session.
 * Prefer `{ token, sessionDurationMs }` from the pii/endTime response so the countdown matches the backend.
 */
export const piiTokenAtom = atom(
  (get) => {
    const endTime = get(piiSessionEndTimeAtom);
    const token = get(piiTokenStorageAtom);
    const now = Date.now();
    if (endTime == null || endTime <= now || !token) {
      return null;
    }
    return token;
  },
  (_get, set, value: PiiTokenSetValue) => {
    const wallNow = Date.now();
    if (value && typeof value === 'object' && 'token' in value) {
      set(piiTokenStorageAtom, value.token);
      set(piiSessionEndTimeAtom, wallNow + value.sessionDurationMs);
      return;
    }
    const token = value === false ? null : value;
    set(piiTokenStorageAtom, token);
    if (token) {
      set(piiSessionEndTimeAtom, wallNow + PII_SESSION_DEFAULT_DURATION_MS);
      set(piiEnabledAtom, true);
    } else {
      set(piiSessionEndTimeAtom, 0);
      set(piiEnabledAtom, false);
    }
  },
);
